import { CheckIcon } from "lucide-react";
import * as React from "react";

import { Button, DropdownMenu } from "@/components/ui";
import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";

import { KANBAN_COLUMNS, KanbanColumnId, getKanbanColumnId } from "./kanbanColumns";
import { getValidKanbanDropColumnIds } from "./kanbanDropEvent";
import { stopKanbanPointer } from "./kanbanStaff";
import { KanbanStatusIcon, statusDisplayLabel } from "./kanbanStatus";

interface KanbanStatusPickerProps {
  status: VideoProject.Status;
  statusLabel?: string;
  requiresQa?: boolean | null;
  currentUserId?: number;
  interactive?: boolean;
  disabled?: boolean;
  onSelect?: (columnId: KanbanColumnId) => void;
}

export function KanbanStatusPicker({
  status,
  statusLabel,
  requiresQa,
  currentUserId,
  interactive = true,
  disabled = false,
  onSelect,
}: KanbanStatusPickerProps) {
  const currentColumnId = getKanbanColumnId(status);
  const label = statusDisplayLabel(status, statusLabel);

  const validColumnIds = React.useMemo(
    () =>
      new Set(
        getValidKanbanDropColumnIds(status, {
          requiresQa,
          currentUserId,
        })
      ),
    [currentUserId, requiresQa, status]
  );

  const triggerClassName = "size-6 rounded-sm text-muted-foreground hover:bg-muted";
  const canChange = interactive && !disabled;

  return (
    <div
      data-kanban-interactive=""
      className="pointer-events-auto mt-0.5 shrink-0"
      onPointerDown={stopKanbanPointer}
      onMouseDown={stopKanbanPointer}
      onClick={stopKanbanPointer}
    >
      <DropdownMenu>
        <DropdownMenu.Trigger
          disabled={!canChange}
          render={props => (
            <Button
              {...props}
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={!canChange}
              aria-label={`Status: ${label}`}
              title={label}
              className={cn(triggerClassName, props.className)}
            >
              <KanbanStatusIcon status={status} />
            </Button>
          )}
        />
        {canChange ? (
          <DropdownMenu.Content align="start" className="min-w-52">
            <DropdownMenu.Group>
              <DropdownMenu.Label>Status</DropdownMenu.Label>
              {KANBAN_COLUMNS.map(column => {
                const selected = column.id === currentColumnId;
                const enabled = selected || validColumnIds.has(column.id);
                const itemLabel = selected ? label : column.label;

                return (
                  <DropdownMenu.Item
                    key={column.id}
                    disabled={!enabled}
                    className="gap-2 py-1.5"
                    onClick={() => {
                      if (!selected) onSelect?.(column.id);
                    }}
                  >
                    {selected ? <KanbanStatusIcon status={status} /> : <KanbanStatusIcon columnId={column.id} />}
                    <span className="truncate">{itemLabel}</span>
                    {selected ? <CheckIcon className="ml-auto size-3.5" /> : null}
                  </DropdownMenu.Item>
                );
              })}
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        ) : null}
      </DropdownMenu>
    </div>
  );
}
